import { useState, useEffect, useRef } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";
import { Send, MessageSquare, Loader2, Check, CheckCheck, Clock, User } from "lucide-react";
import { Select } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import {
    WA_INBOX_URL, WA_CONVERSATIONS_URL, WA_SEND_URL, WA_TEMPLATES_URL, WA_ACCOUNTS_URL, USERS_SIMPLE_URL
} from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";

const StatusTick = ({ status }) => {
    if (status === 'read') return <CheckCheck className="h-3 w-3 text-blue-400" />;
    if (status === 'delivered') return <CheckCheck className="h-3 w-3 text-gray-400" />;
    if (status === 'sent') return <Check className="h-3 w-3 text-gray-400" />;
    return <Clock className="h-3 w-3 text-gray-300" />;
};

export default function WhatsAppInbox() {
    const { fetch, create, update } = useApiMethods();
    const [conversations, setConversations] = useState([]);
    const [selectedConv, setSelectedConv] = useState(null);
    const [messages, setMessages] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [msgLoading, setMsgLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [messageText, setMessageText] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [assignLoading, setAssignLoading] = useState(false);
    const bottomRef = useRef(null);

    const loadInbox = async () => {
        setLoading(true);
        const [cRes, tRes, aRes, uRes] = await Promise.all([
            fetch(WA_INBOX_URL),
            fetch(WA_TEMPLATES_URL),
            fetch(WA_ACCOUNTS_URL),
            fetch(USERS_SIMPLE_URL),
        ]);
        if (cRes?.status === 'success') setConversations(cRes.data || []);
        if (tRes?.status === 'success') setTemplates((tRes.data || []).filter(t => t.status === 'approved'));
        if (aRes?.status === 'success') setAccounts(aRes.data || []);
        if (uRes?.status === 'success') setUsers(uRes.data || []);
        setLoading(false);
    };

    useEffect(() => { loadInbox(); }, []);

    const openConversation = async (conv) => {
        setSelectedConv(conv);
        setMsgLoading(true);
        setSelectedTemplate(null);
        setMessageText('');
        const res = await fetch(`${WA_CONVERSATIONS_URL}${conv.id}/`);
        if (res?.status === 'success') {
            setMessages(res.data?.messages || []);
            // Refresh 24h window from server response
            setSelectedConv(prev => ({ ...prev, is_24h_window_open: res.data?.is_24h_window_open }));
        }
        setMsgLoading(false);
        // Mark as read in list
        setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c));
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    };

    const handleSend = async () => {
        if (!selectedConv) return;
        const account = accounts[0];
        if (!account) { ToastMsg('error', 'No WhatsApp account connected'); return; }

        const windowOpen = selectedConv.is_24h_window_open;
        if (!windowOpen && !selectedTemplate) {
            ToastMsg('error', 'Session window is closed — please select a template');
            return;
        }
        if (windowOpen && !messageText.trim() && !selectedTemplate) return;

        setSending(true);
        const payload = {
            account_id: account.id,
            to_phone: selectedConv.customer_phone,
            customer_id: selectedConv.customer_id,
            customer_name: selectedConv.customer_name,
            message_type: selectedTemplate ? 'template' : 'text',
            body_text: messageText,
            template_id: selectedTemplate?.id || null,
        };
        const res = await create(WA_SEND_URL, payload);
        setSending(false);
        if (res?.status === 'success') {
            setMessageText('');
            setSelectedTemplate(null);
            openConversation(selectedConv);
        } else {
            ToastMsg('error', res?.message || 'Send failed');
        }
    };

    const handleAssign = async (userId) => {
        if (!selectedConv) return;
        setAssignLoading(true);
        const res = await update(`${WA_CONVERSATIONS_URL}${selectedConv.id}/`, { assigned_to_id: userId });
        setAssignLoading(false);
        if (res?.status === 'success') {
            setConversations(prev => prev.map(c => c.id === selectedConv.id ? { ...c, assigned_to_id: userId } : c));
            setSelectedConv(prev => ({ ...prev, assigned_to_id: userId }));
        }
    };

    return (
        <div className="p-6 h-[calc(100vh-80px)]">
            <div className="flex h-full gap-4">
                {/* Conversation list */}
                <div className="w-72 flex-shrink-0 flex flex-col">
                    <ContainerCard className="flex-1 flex flex-col overflow-hidden p-0">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                                <MessageSquare className="h-4 w-4 text-green-500" />
                                Inbox
                            </h3>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {loading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                                </div>
                            ) : conversations.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-8">No conversations yet</p>
                            ) : (
                                conversations.map(conv => (
                                    <button
                                        key={conv.id}
                                        onClick={() => openConversation(conv)}
                                        className={cn(
                                            "w-full text-left px-4 py-3 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors",
                                            selectedConv?.id === conv.id && "bg-green-50 dark:bg-green-900/10"
                                        )}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                                                    {conv.customer_name || conv.customer_phone}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{conv.customer_phone}</p>
                                                <p className="text-xs text-gray-400 truncate mt-0.5">{conv.last_message_preview}</p>
                                            </div>
                                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                {conv.last_message_at && (
                                                    <span className="text-[10px] text-gray-400">
                                                        {new Date(conv.last_message_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                )}
                                                {conv.unread_count > 0 && (
                                                    <span className="w-4 h-4 rounded-full bg-green-500 text-white text-[10px] flex items-center justify-center font-bold">
                                                        {conv.unread_count}
                                                    </span>
                                                )}
                                                {!conv.is_24h_window_open && (
                                                    <span className="text-[9px] text-orange-400 bg-orange-50 dark:bg-orange-900/10 px-1 rounded">24h closed</span>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    </ContainerCard>
                </div>

                {/* Message thread */}
                <div className="flex-1 flex flex-col">
                    {!selectedConv ? (
                        <ContainerCard className="flex-1 flex items-center justify-center">
                            <div className="text-center">
                                <MessageSquare className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                                <p className="text-sm text-gray-400">Select a conversation</p>
                            </div>
                        </ContainerCard>
                    ) : (
                        <ContainerCard className="flex-1 flex flex-col overflow-hidden p-0">
                            {/* Header */}
                            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                                <div>
                                    <p className="font-semibold text-gray-800 dark:text-gray-100">
                                        {selectedConv.customer_name || selectedConv.customer_phone}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{selectedConv.customer_phone}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    {!selectedConv.is_24h_window_open && (
                                        <span className="text-xs bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400 px-2 py-0.5 rounded-full">
                                            Session window closed — template only
                                        </span>
                                    )}
                                    <div className="flex items-center gap-1">
                                        <User className="h-4 w-4 text-gray-400" />
                                        <Select
                                            size="small"
                                            placeholder="Assign to..."
                                            className="w-36"
                                            value={selectedConv.assigned_to_id || undefined}
                                            loading={assignLoading}
                                            options={users.map(u => ({ value: u.id, label: `${u.login_fname || ''} ${u.login_lname || ''}`.trim() }))}
                                            onChange={handleAssign}
                                            allowClear
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Messages */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50 dark:bg-gray-900/50">
                                {msgLoading ? (
                                    <div className="flex items-center justify-center py-12">
                                        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                                    </div>
                                ) : messages.length === 0 ? (
                                    <p className="text-sm text-gray-400 text-center py-8">No messages</p>
                                ) : (
                                    messages.map(msg => {
                                        const isSent = msg.direction === 'sent';
                                        return (
                                            <div key={msg.id} className={cn("flex", isSent ? "justify-end" : "justify-start")}>
                                                <div className={cn(
                                                    "max-w-xs lg:max-w-md px-3 py-2 rounded-2xl text-sm shadow-sm",
                                                    isSent
                                                        ? "bg-[#dcf8c6] dark:bg-green-900/40 rounded-tr-none text-gray-800 dark:text-gray-100"
                                                        : "bg-white dark:bg-gray-800 rounded-tl-none text-gray-800 dark:text-gray-100"
                                                )}>
                                                    <p className="whitespace-pre-wrap">{msg.body_text || `[${msg.message_type}]`}</p>
                                                    <div className={cn("flex items-center gap-1 mt-1", isSent ? "justify-end" : "justify-start")}>
                                                        <span className="text-[10px] text-gray-400">
                                                            {msg.sent_at ? new Date(msg.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                                        </span>
                                                        {isSent && <StatusTick status={msg.status} />}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                <div ref={bottomRef} />
                            </div>

                            {/* Compose */}
                            <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 space-y-2">
                                {!selectedConv.is_24h_window_open && (
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Select a template to send:</p>
                                        <Select
                                            className="w-full"
                                            placeholder="Choose approved template..."
                                            value={selectedTemplate?.id}
                                            options={templates.map(t => ({ value: t.id, label: t.template_name }))}
                                            onChange={v => setSelectedTemplate(templates.find(t => t.id === v) || null)}
                                        />
                                        {selectedTemplate && (
                                            <p className="text-xs text-gray-500 mt-1 bg-gray-50 dark:bg-gray-800 rounded p-2 whitespace-pre-wrap">
                                                {selectedTemplate.body_text}
                                            </p>
                                        )}
                                    </div>
                                )}
                                {selectedConv.is_24h_window_open && (
                                    <div className="flex items-center gap-2">
                                        <Input
                                            value={messageText}
                                            onChange={e => setMessageText(e.target.value)}
                                            placeholder="Type a message..."
                                            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                                            className="flex-1"
                                        />
                                        <Button
                                            onClick={handleSend}
                                            disabled={sending || (!messageText.trim() && !selectedTemplate)}
                                            className="flex items-center gap-1 bg-green-500 hover:bg-green-600"
                                        >
                                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                        </Button>
                                    </div>
                                )}
                                {!selectedConv.is_24h_window_open && selectedTemplate && (
                                    <Button
                                        onClick={handleSend}
                                        disabled={sending}
                                        className="w-full flex items-center gap-2 bg-green-500 hover:bg-green-600"
                                    >
                                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                        Send Template
                                    </Button>
                                )}
                            </div>
                        </ContainerCard>
                    )}
                </div>
            </div>
        </div>
    );
}
